export type PurchaseLineSourcingInput = {
  unit_price?: number | string | null;
  unit_cost?: number | string | null;
  purchase_supplier_mapping_id?: number | null;
  purchase_supplier_contact_id?: number | null;
  purchase_supplier_name?: string | null;
  purchase_supplier_sku?: string | null;
  purchase_supplier_currency?: string | null;
  purchase_supplier_cost_price?: number | string | null;
  purchase_supplier_is_preferred?: boolean | null;
  purchase_supplier_priority_rank?: number | string | null;
  purchase_supplier_lead_time_days?: number | string | null;
  purchase_supplier_selection_reason?: string | null;
  purchase_supplier_fallback_reason?: string | null;
  purchase_supplier_price_source?: string | null;
  purchase_supplier_matches_document_contact?: boolean | null;
};

export interface PurchaseLineSourcingSummary {
  supplierMappingId: number | null;
  supplierContactId: number | null;
  supplierName: string | null;
  supplierSku: string | null;
  supplierCurrency: string | null;
  supplierCostPrice: number | null;
  isPreferred: boolean;
  priorityRank: number | null;
  leadTimeDays: number | null;
  reason: string | null;
  priceSource: string | null;
  priceSourceLabel: string;
  matchesDocumentSupplier: boolean;
  differsFromDocumentSupplier: boolean;
  lineUnitPrice: number;
  varianceAmount: number | null;
  variancePercent: number | null;
  hasVariance: boolean;
}

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const next = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(next) ? next : null;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getPurchaseLineSourcingSummary(
  item: PurchaseLineSourcingInput,
  documentSupplierContactId?: number | null,
): PurchaseLineSourcingSummary | null {
  const supplierMappingId = toFiniteNumber(item.purchase_supplier_mapping_id);
  const supplierContactId = toFiniteNumber(item.purchase_supplier_contact_id);
  const supplierCostPrice = toFiniteNumber(item.purchase_supplier_cost_price);
  const lineUnitPrice = toFiniteNumber(item.unit_price ?? item.unit_cost) ?? 0;
  const supplierName = typeof item.purchase_supplier_name === 'string' && item.purchase_supplier_name.trim()
    ? item.purchase_supplier_name.trim()
    : supplierContactId
      ? `Supplier #${supplierContactId}`
      : null;

  if (supplierMappingId == null && supplierContactId == null && supplierName == null && supplierCostPrice == null) {
    return null;
  }

  const priceSource = typeof item.purchase_supplier_price_source === 'string' ? item.purchase_supplier_price_source : null;
  const priceSourceLabel = priceSource === 'selected_preferred_supplier'
    ? 'Selected supplier · preferred'
    : priceSource === 'selected_supplier_mapping'
      ? 'Selected supplier mapped'
      : priceSource === 'preferred_supplier_mapping'
        ? 'Preferred source'
        : priceSource === 'supplier_mapping'
          ? 'Mapped supplier'
          : item.purchase_supplier_is_preferred
            ? 'Preferred source'
            : 'Sourced supplier';

  const normalizedDocumentSupplierId = toFiniteNumber(documentSupplierContactId);
  const matchesDocumentSupplier = item.purchase_supplier_matches_document_contact === true
    || (normalizedDocumentSupplierId != null && supplierContactId != null && normalizedDocumentSupplierId === supplierContactId);
  const differsFromDocumentSupplier = Boolean(
    normalizedDocumentSupplierId != null
    && supplierContactId != null
    && normalizedDocumentSupplierId !== supplierContactId
  );

  const varianceAmount = supplierCostPrice == null ? null : roundCurrency(lineUnitPrice - supplierCostPrice);
  const variancePercent = supplierCostPrice == null || supplierCostPrice === 0
    ? null
    : roundCurrency((varianceAmount! / supplierCostPrice) * 100);
  const hasVariance = varianceAmount != null && Math.abs(varianceAmount) >= 0.01;
  const reason = (typeof item.purchase_supplier_selection_reason === 'string' && item.purchase_supplier_selection_reason.trim())
    || (typeof item.purchase_supplier_fallback_reason === 'string' && item.purchase_supplier_fallback_reason.trim())
    || null;

  return {
    supplierMappingId,
    supplierContactId,
    supplierName,
    supplierSku: typeof item.purchase_supplier_sku === 'string' && item.purchase_supplier_sku.trim()
      ? item.purchase_supplier_sku.trim()
      : null,
    supplierCurrency: typeof item.purchase_supplier_currency === 'string' && item.purchase_supplier_currency.trim()
      ? item.purchase_supplier_currency.trim()
      : null,
    supplierCostPrice,
    isPreferred: item.purchase_supplier_is_preferred === true,
    priorityRank: toFiniteNumber(item.purchase_supplier_priority_rank),
    leadTimeDays: toFiniteNumber(item.purchase_supplier_lead_time_days),
    reason,
    priceSource,
    priceSourceLabel,
    matchesDocumentSupplier,
    differsFromDocumentSupplier,
    lineUnitPrice,
    varianceAmount,
    variancePercent,
    hasVariance,
  };
}
